// Better Feature client context for baseURL
import { useBetterFeatureClient } from "@di-devs/utils-fe";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AUDIENCE_CONSTANTS } from "../../../shared/constants/audience.constants";
import type {
	AudienceCreateType,
	AudienceSearchType,
	ListAudiencesQueryParams,
} from "../../../shared/types/audience.types";
import type { FeatureClient } from "../../shared/types";

/**
 * Hook to manage audience list using Better Feature client baseURL
 */
export const useAudiences = (query?: ListAudiencesQueryParams) => {
	// Get Better Feature client context for proper baseURL
	const featureClient = useBetterFeatureClient<FeatureClient>();

	return useQuery({
		queryKey: [AUDIENCE_CONSTANTS.QUERY_KEY_AUDIENCES, query],
		queryFn: async () => {
			// Use the Better Feature client's API method
			const response = await featureClient.listAudiences(query);

			// Better Feature client returns response with data property
			// TODO: Investigate whether stripping error from response here instead of propagating error value up
			return response;
		},
	});
};

/**
 * Hook to search audience members using Better Feature client
 */
export const useSearchAudience = () => {
	// Get Better Feature client context for proper baseURL
	const featureClient = useBetterFeatureClient<FeatureClient>();

	return useMutation({
		mutationFn: async (searchParams: AudienceSearchType) => {
			const response = await featureClient.searchAudience(searchParams);
			return response;
		},
	});
};

/**
 * Hook to create a new audience using Better Feature client
 */
export const useCreateAudience = () => {
	const queryClient = useQueryClient();

	// Get Better Feature client context for proper baseURL
	const featureClient = useBetterFeatureClient<FeatureClient>();

	return useMutation({
		mutationFn: async (createParams: AudienceCreateType) => {
			// Use the Better Feature client's API method
			const response = await featureClient.createAudience(createParams);
			return response;
		},
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: [AUDIENCE_CONSTANTS.QUERY_KEY_AUDIENCES],
			});
		},
	});
};

/**
 * Hook to delete an audience using Better Feature client
 */
export const useDeleteAudience = () => {
	const queryClient = useQueryClient();

	// Get Better Feature client context for proper baseURL
	const featureClient = useBetterFeatureClient<FeatureClient>();

	return useMutation({
		mutationFn: async (audienceGroupId: string) => {
			// Use the Better Feature client's API method
			const response = await featureClient.deleteAudience(audienceGroupId);
			return response;
		},
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: [AUDIENCE_CONSTANTS.QUERY_KEY_AUDIENCES],
			});
		},
	});
};

// Hook for SSE event subscription (if needed)
export const useAudienceSSE = (_handler?: () => void) => {
	// This would need to be implemented based on your SSE setup
	// For now, it's a placeholder
	return {
		subscribe: () => {
			// SSE subscription logic
		},
		unsubscribe: () => {
			// SSE subscription logic
		},
	};
};
